import React, {useCallback, useEffect, useMemo, useState} from "react";
import Project from "../models/Project";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {matchPath, useLocation, useNavigate, useParams} from "react-router-dom";
import {useMount, useUnmount} from "react-use";
import ProjectPosition from "../models/ProjectPosition";
import User from "../models/User";
import {useAuth} from "./AuthProvider";
import RequestSOWModal from "../views/project-bidding/components/RequestSOWModal";
import {ProjectFixedSummary} from "../views/project-bidding/components/ProjectSummary";
import Component from "../components/Component";
import {useAPI} from "../config/API";

let ProjectContext = React.createContext(null);

const MAX_BULK_ACTIONS = 15;
const ClientProjectProvider = ({children}) => {

    const {projectId, "*": projectRouting} = useParams();
    const queryClient = useQueryClient();
    const {auth, update: updateAuth, appropriate} = useAuth();
    const navigate = useNavigate();
    const {pathname, search} = useLocation();
    const [bulkActions, setBulkActions] = useState([]);

    const {
        data: projectData,
        isFetching,
        isInitialLoading: isLoading,
        isRefetching,
        error,
        refetch
    } = useAPI(['fetchProject', parseInt(projectId)],  {
            enabled: !!projectId && projectId !== 'new'
        }
    )
    const routeStep = useMemo(() => {
        const matchedRoute = matchPath({path: 'client/project/:status/:projectId/*'}, pathname);
        return matchedRoute?.params?.['*']
    }, [pathname]);

    useEffect(() => {
        if (isLoading || !projectData) {
            return;
        }

        if (projectData?.isCancelled() && (pathname.includes('bidding') || pathname.includes('project/active'))) {
            return navigate('/client/projects/cancelled', {replace: true})
        }
        if (projectData &&
            (
                (pathname?.includes('bidding') && !projectData.isBidding())
                || (!pathname?.includes('project/active') && projectData.isActive())
                || (pathname?.includes('project/active') && !projectData.isActive())
                ||  (pathname.includes('bidding') && !routeStep)
            )) {

            return navigate(
                project.detailLink(), {replace: true}
            );
        }else if (pathname?.includes('bidding') && projectData.hasInvalidPositions() && !project.detailLink()?.includes('bidding')) {
            return navigate(
                project.detailLink(), {replace: true}
            )
        }
    },[projectData?.status, projectData?.hasInvalidPositions?.(), isLoading, projectRouting])

    // console.log('ClientProjectProvider', projectData?.hasInvalidPositions?.())
    const updateCallback = async (updatedProject) => {
        // console.log('ClientProjectProvider@updateCallback', updatedProject.id)
        if (parseInt(updatedProject?.id) === parseInt(projectId)) {
            await queryClient.setQueryData(['fetchProject', parseInt(projectId)], previousData => {
                if (!previousData) return undefined;

                return Project.make({...updatedProject})
            })
        }
    }

    const updateProjectMutation = useMutation(['updateProject', parseInt(projectId)],
        (params) => {
            if (params?.id === 'new') {
                let newProject = {
                    name: params.name,
                    type: params.type,
                    posts_on_talent_job_board: params.posts_on_talent_job_board,
                    assigned_users: params.assigned_users,
                }
                newProject.service_id = params.service ? parseInt(
                    params.service.id
                ) : null;

                newProject.type = parseInt(params.type);

                if (TB.isDefined(params.client_project_group_id)) {
                    newProject.client_project_group_id = params.client_project_group_id;
                    if (params.client_project_group_id !== auth?.last_client_project_group_id) {
                        updateAuth(User.make({
                            ...(auth || {}),
                            last_client_project_group_id: project.client_project_group_id
                        }))
                    }
                }

                return Project.create(newProject);
            } else return project?.update(params);
        }, {
            onError: async (e) => {
                await setIsDisabled(false)
                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedProject) => {
                await setIsDisabled(false)
                if (!updatedProject) {
                    TB.showServerErrorToast()
                    return false;
                }
                // await updateCallback(updatedProject);
            }
        }
    )

    const updateProjectSOWSettingsMutation = useMutation(['updateProjectSOWSettings', parseInt(projectId)],
        (params) => {
            return project.setClientLeadUpdatesForProject(params);
        }, {
            onError: async (e) => {
                await setIsDisabled(false)
                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedProject) => {
                await setIsDisabled(false)
                if (!updatedProject) {
                    TB.showServerErrorToast()
                    return false;
                }
                // await updateCallback(updatedProject);
            }
        }
    )

    const markProjectAsCompleteMutation = useMutation(['markProjectAsComplete', parseInt(projectId)],
        (params) => {
            return project.markProjectAsComplete();
        }, {
            onError: async (e) => {
                await setIsDisabled(false)
                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedProject) => {
                await setIsDisabled(false)
                if (!updatedProject) {
                    TB.showServerErrorToast()
                    return false;
                }
                // await updateCallback(updatedProject);
            }
        }
    )

    const runBulkActionsMutation = useMutation(['runBulkActions', parseInt(projectId)],
        ({actionType, positionId, params}) => {
            return project.runBulkActionsForCandidates(actionType, positionId, params);
        }, {
            onError: async (e) => {
                await setIsDisabled(false)
                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedProject) => {
                await TB.wait(250)
                await setIsDisabled(false)
                setBulkActions([])
                // await updateCallback(updatedProject);
            }
        }
    )

    const setAppSheetLinkingForProject = async (type, params) => {
        if (!auth || !auth.canManageClients()) {
            throw new Error("Unauthorized")
        }

        // console.log(projectData, 'projectData')
        // return false;

        await setIsDisabled(true)
        let requestURL = `/api/appsheet/clients/${auth.clientOrg().id}`

        let appendLinkType = params?.appsheet_id ? 'link' : 'unlink';
        let requestParams = params?.appsheet_id ? {appsheet_id: params.appsheet_id} : {};
        switch (type) {
            case 'client':
                requestURL += `/${appendLinkType}`
                break;
            case 'project':
                requestURL += `/projects/${projectData.id}/${appendLinkType}`
                break;
            case 'position':
                if (!params.positionId) {
                    throw new Error("Missing position id")
                }
                requestURL += `/projects/${projectData?.id}/positions/${params.positionId}/${appendLinkType}`
                break;
            default:
                throw new Error("Link type not valid")
        }
        let setAppSheetLinkingForProjectRequest = TB.request(
            TB.api_endpoint(requestURL), requestParams
        )

        let response = await setAppSheetLinkingForProjectRequest.post().catch(console.warn);

        if(response?.success?.status !== 200) {
            throw new Error("Something went wrong")
        }
        return response;
    }


    const setAppSheetLinkingMutation = useMutation(['setAppSheetLinking', parseInt(projectId)],
        ({actionType, params}) => {
            return setAppSheetLinkingForProject(actionType, params)
        }, {
            onError: async (e) => {
                await setIsDisabled(false)
                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedProject) => {
                await TB.wait(250)
                await setIsDisabled(false)
                if (updatedProject) {
                    TB.toast('AppSheet Link set successfully')
                    return true
                }
            }
        }
    )

    const runAppSheetLinkRequest = async (actionType, appsheet_id = false, positionId) => {
        if (!auth.canManageClients()) {
            return;
        }

        await setIsDisabled(true);

        let response = await setAppSheetLinkingMutation.mutateAsync({actionType, params: {appsheet_id, positionId}});

        await setIsDisabled(false);

        return response;
    }

    const [uniqId, setUniqId] = useState(TB.uuid());

    const [isDisabled, setIsDisabled] = useState(false);

    useMount(() => {
        TB.event().listen("Projects@updated", updateCallback, uniqId)
    })

    useUnmount(() => {
        TB.event().provider().off("Projects@updated", updateCallback, uniqId)
    })

    const postUpdate = async (projectProps) => {
        await setIsDisabled(true)
        console.log('postUpdate', projectProps)
        let updatedProject = await updateProjectMutation.mutateAsync(projectProps)
        if (!updatedProject) {
            let PostRequest = projectProps?.id === 'new' ? Project.CreateRequest : Project.SyncRequest
            PostRequest.didHandleError = await PostRequest.handleError();
            if (PostRequest.didHandleError
                && PostRequest.shouldRerun) {
                return postUpdate(projectProps)
            }
        }

        return updatedProject;
    }

    const updateProjectPosition = async (positionProps, postToBackend = true) => {
        const positionIndex = TB.findIndex(project?.positions, ['id', positionProps.id]);

        if (positionIndex === -1) {
            return TB.showServerErrorToast();
        }

        let update = TB.set({...(project || {})}, `positions[${positionIndex}]`,
            ProjectPosition.make({...project?.positions[positionIndex], ...positionProps})
        );

        if (!postToBackend) {
            return update;
        }

        return postUpdate(update)
    }

    const requestSOW = async () => {
        if (project.isBYO() && !project.hasFilledAllPositions()) {
            return TB.sysAlert('byo.request_sow_button_clicked_roles_not_selected');
        }
        return TB.event("Modal@show", {
            modalClass: "request-sow-modal",
            content: (
                <RequestSOWModal
                    onConfirm={async () => {
                        await setIsDisabled(true)
                        let wasSuccess = await project.requestSOW();

                        await setIsDisabled(false)

                        if (!wasSuccess) {
                            return TB.showServerErrorToast();
                        }

                        return TB.sysAlert('byo.request_sow_button_clicked')
                    }}
                >

                    <ProjectFixedSummary project={project}/>
                </RequestSOWModal>
            )
        })
    }

    const cancelSOW = async () => {
        if (!project.hasRequestedSOW()) {
            return;
        }
        let confirm = await TB.sysAlert('byo.cancel_sow_button_click')
        if (confirm) {
            await setIsDisabled(true)

            let wasSuccess = await project.cancelSOW();

            await setIsDisabled(false)

            if (!wasSuccess) {
                return;
            }

            return TB.sysAlert('byo.project_sow_cancelled');
        }
    }

    const setContractAvailableForClient = async () => {
        if (!auth?.canManageClients() || !project.isAwaitingContract()) {
            return;
        }
        let confirm = await TB.sysAlert('byo.client_lead_make_client_lead_available');
        if (confirm) {
            await setIsDisabled(true)

            let projectUpdated = await project.setContractAvailableForClient();

            await setIsDisabled(false)

            if (projectUpdated && projectUpdated instanceof Project) {
                return TB.toast('Project status moved to pending contract. Client has been notified.');
            }
        }
    }

    const setClientLeadUpdatesForProject = async (props) => {
        if (!auth?.canManageClients() || !(project.isAwaitingContract() || project.isAwaitingKickoff())) {
            return;
        }
        let confirm;
        console.log('setClientLeadUpdatesForProject', props)
        if (props.is_active) {
            confirm = await TB.alert('warning',
                'Make Project Active',
                'Are you sure you want to make this project active? Note: this cannot be undone.',
                [{title: 'Yes, Continue'}, {title: 'Cancel'}]
            );
        }
        // else if (props.client_contract_management_setting === 1) {
        //     confirm = await TB.sysAlert('byo.client_lead_make_client_lead_available');
        // }
        else {
            confirm = true;
        }
        if (confirm) {
            await setIsDisabled(true)

            let projectUpdated = await updateProjectSOWSettingsMutation.mutateAsync(props);

            await setIsDisabled(false)

            if (projectUpdated && projectUpdated instanceof Project) {
                let message = '';
                if (props.is_active) {
                    message += 'Project status moved to active.'
                }else if (props.client_contract_management_setting === Project.ContractSettings.ApprovedOnline) {
                    message += 'Project status moved to pending contract.'
                }else if (props.client_contract_management_setting) {
                    message += 'Project status moved to awaiting kickoff.'
                }
                if (props.talent_emails_enabled && props.client_emails_enabled) {
                    message += ' Client and Talent have been notified'
                }else if (props.client_emails_enabled) {
                    message += ' Client has been notified.';
                }else if (props.talent_emails_enabled) {
                    message += ' Client has been notified.';
                }
                TB.toast(message);
            }
            return projectUpdated;
        }
        return false;
    }

    const setNotificationUpdatesForProject = async (props) => {
        if (!auth?.canManageClients()) {
            return;
        }

        await setIsDisabled(true)

        let projectUpdated = await project.setNotificationUpdatesForProject(props);

        await setIsDisabled(false)

        if (projectUpdated && projectUpdated instanceof Project) {
            TB.toast("Project notification settings updated");
            return true
        }
        return false
    }

    const deleteProjectContract = async () => {
        if (!auth.canManageClients()) {
            return;
        }

        await setIsDisabled(true);

        let projectUpdated = await updateProjectSOWSettingsMutation.mutateAsync({reset_settings: true});

        await setIsDisabled(false);

        return projectUpdated;
    }

    const newProject = useCallback(() => {
        if (projectId !== 'new') return null;
        let assignedUser = TB.pick(auth || {}, ['id', 'first_name', 'last_name', 'client_project_groups', 'email']);

        assignedUser = User.make({
            ...assignedUser,
            is_owner: true
        })
        let projectModel = {
            id: 'new',
            assigned_users: [assignedUser]
        }
        // TODO Change with updated platform features for teams
        // if (!laravel.platform_features.clients.services) {
        //     projectModel.type = TB.invert(laravel.project_types)[Project.ProjectType.BYO]
        // }
        return Project.make({...projectModel})
    }, [projectId, auth]);

    const project = projectData || newProject();
    const value = {
        project,
        isLoading,
        isDisabled,
        error: error ? Component.parseRequestError(
            Project.FetchRequest, {
                500: 'error_show_project',
                404: 'error_project_not_found',
            }
        ) : null,
        onAddToBulkActions: (candidate) => {

            let updatedActions = TB.xorBy([...bulkActions], [candidate], 'id');
            if (updatedActions?.length >= MAX_BULK_ACTIONS) {
                return TB.toast("Max of 10 candidates can be selected at a time", {theme: 'danger'});
            }
            setBulkActions(updatedActions)
        },
        bulkActions,
        onRunBulkActions: (actionType, positionId, params = {}) => {
            return runBulkActionsMutation.mutate({actionType,positionId, params: {...params, talent_ids: TB.map(bulkActions, 'id')}})
        },
        onClearBulkActions: () => setBulkActions([]),
        refetch,
        postUpdate,
        update: (props) => updateCallback(props),
        isProjectUpdating: isDisabled ||
            updateProjectMutation.isLoading ||
            updateProjectSOWSettingsMutation.isLoading ||
            runBulkActionsMutation?.isLoading ||
            setAppSheetLinkingMutation?.isLoading ||
            markProjectAsCompleteMutation?.isLoading,
        projectError: error,
        refreshProject: refetch,
        updateProjectPosition,
        submitProjectUpdate: postUpdate,
        requestSOW,
        cancelSOW,
        markProjectAsComplete: async () => {
            if (!project.isActive()) {
                TB.toast("Cannot be completed at this time. Please try again later", {type: 'error'});
                return false;
            }
            let success = await markProjectAsCompleteMutation?.mutate({});
            console.log('markProjectAsComplete', success)
            return success;
        },
        deleteProjectContract,
        setContractAvailableForClient,
        setClientLeadUpdatesForProject,
        setNotificationUpdatesForProject,
        linkAppSheetForClient: (appSheetId) => runAppSheetLinkRequest('client', appSheetId),
        unlinkAppSheetForClient: () => runAppSheetLinkRequest('client'),
        linkAppSheetForProject: (appSheetId) => runAppSheetLinkRequest('project', appSheetId),
        unlinkAppSheetForProject: () => runAppSheetLinkRequest('project'),
        linkAppSheetForProjectPosition: (appSheetId, positionId) => runAppSheetLinkRequest('position', appSheetId, positionId),
        unlinkAppSheetForProjectPosition: (positionId) => runAppSheetLinkRequest('position', false, positionId),
    }


    return (
        <ProjectContext.Provider value={value}>
            {children}
        </ProjectContext.Provider>
    );
}

const useProject = () => {
    return React.useContext(ProjectContext);
}
export default ClientProjectProvider;
export {
    useProject,
    ProjectContext
}